import cn from 'classnames';
import { Button, Tabs } from 'Components';
import { PromptHelpers } from 'Components/Prompt/Prompt';
import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import ModuleSettings from './tabs/ModuleSettings/ModuleSettings';
import {
	DynamicPermissionCode,
	PermissionCode,
	TwoFactorAuthorizationStatus
} from 'Services/Api/Users/Types';
import { usePermission } from 'Components/Permission/Permission.hooks';
import UserContextProvider from '../UserContext';
import styles from './EditUser.module.scss';
import { useTranslation } from 'react-i18next';
import { TabItem } from 'app-types';
import Notification from '../Profile/NewLayout/Notification';
import AccountSettings from '../Profile/NewLayout/Account';
import ChangePasswordModal from '../Profile/NewLayout/ChangePasswordModal';
import SaveChangesModal from '../Profile/NewLayout/SaveChangesModal';
import SaveButton from '../Profile/NewLayout/SaveButton';
import { useLocation } from 'react-router-dom';
import EnableMFAModal from '../Profile/NewLayout/EnableMfaModal';
import { useStores } from 'Hooks/useStore';
import { SystemRoleType } from 'Services/Api/Roles/Types';

export enum TabKey {
	Profile = 'Profile',
	Settings = 'Settings',
	ModuleSettings = 'ModuleSettings',
	ChangePassword = 'Change Password',
	Notifications = 'Notifications'
}

type UserTab = TabItem<TabKey>;

const TABS: UserTab[] = [
	{
		key: TabKey.Profile,
		tab: 'profile.notifications.account'
	},
	{
		key: TabKey.Notifications,
		tab: 'profile.notifications.tab',
		permission: { code: PermissionCode.UserNotifications }
	},
	{
		key: TabKey.ModuleSettings,
		tab: 'user.editUser.tabs.moduleSettings',
		permission: { code: PermissionCode.Lms }
	}
];

type EditUserTabsProps = PromptHelpers & {
	isProfile: boolean;
	isLmsHasError?: boolean;
	twoFactorAuthenticationStatus: TwoFactorAuthorizationStatus;
};

type ModalType = 'changePassword' | 'save' | 'enableMFA' | null;
function EditUserTabs({
	setBlockTransition,
	isProfile,
	isLmsHasError,
	twoFactorAuthenticationStatus
}: EditUserTabsProps) {
	const [showModal, setShowModal] = useState<ModalType>(null);
	const { t } = useTranslation();
	const { dirty: currentDirty, isSubmitting: currentIsSubmitting } = useFormikContext();
	const { authStore } = useStores();
    const isUserRole = authStore.currentUser?.primarySystemRole?.role === SystemRoleType.User;
	const permission = usePermission();
	const { pathname } = useLocation();
	const isAddNewUser = pathname.includes('/users/new');

	const closeModal = () => setShowModal(null);

	useEffect(() => {
		const globalBlockTransitoion = currentDirty && !currentIsSubmitting;
		setBlockTransition(globalBlockTransitoion);
		if (!currentIsSubmitting && showModal === 'save') {
			closeModal();	
		}
	}, [setBlockTransition, currentDirty, currentIsSubmitting]);

	const getTabContent = (tab: UserTab) => {
		let content: React.ReactNode = null;
		switch (tab.key) {
			case TabKey.Profile:
				content = <AccountSettings {...{ isProfile, isAddNewUser, isUserRole, twoFactorAuthenticationStatus }} openModal={(val: ModalType) => setShowModal(val)} />
				break;
			case TabKey.Notifications:
				if (isProfile)
					content = (
						<Notification openModal={() => setShowModal('save')} />
					);
				break;
			case TabKey.ModuleSettings:
				content = (
					<ModuleSettings isProfile={isProfile} />
				);
				break;
		}
		return content;
	};

	return (
		<UserContextProvider permissionCode={DynamicPermissionCode.EditUser}>
			{(isProfile && !isUserRole) ? <Tabs
				defaultActiveKey={TabKey.Profile}
				className={cn(styles.accountTabs, {
					[styles.moduleSettingsErrorTab]: isLmsHasError
				})}
			>
				{TABS.map((tab) => {
					const hasPermission =
						!tab.permission ||
						permission.getHasPermission(tab.permission).permitted;
					if (!hasPermission) {
						return null;
					}
					const content = getTabContent(tab);
					if (!content) {
						return null;
					}
					return (
						<Tabs.TabPane tab={t(tab.tab)} key={tab.key}>
							{content}
						</Tabs.TabPane>
					);
				})}
			</Tabs> : <div className={styles.accountTabs}>
				<div className='ant-tabs-content-holder'>
					<div className={`ant-tabs-content ${!isAddNewUser ? "add-bottom-space" : ""}`}>
						<div className='ant-tabs-tabpane'>
							{getTabContent(TABS[0])}
							<SaveButton saveChanges={() => setShowModal("save")} />
						</div>
					</div>
				</div>
			</div>}
			{showModal === 'changePassword' ? (
				<ChangePasswordModal onClose={closeModal} />
			) : null}
			{showModal === 'save' ? (
				<SaveChangesModal
					{...{ isProfile, isAddNewUser }}
					onClose={closeModal}
				/>
			) : null}
			{showModal === 'enableMFA' ? (
				<EnableMFAModal {...{ isProfile }} onClose={closeModal} />
			) : null}
		</UserContextProvider>
	);
}

export default EditUserTabs;
